import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Avatar,
  Input,
  Button,
  InputGroup,
  Flex,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Image,
  Link,
  Divider,
  useToast,
  Spinner
} from '@chakra-ui/react';
import { useColorModeValue } from '@chakra-ui/react';
import { FaDiscord, FaExternalLinkAlt, FaUsers, FaComments, FaHashtag, FaVolumeUp, FaMicrophone, FaHeadphones, FaCog } from 'react-icons/fa';
import { useDiscord } from '../context/DiscordContext';

const CommunityChatSidebar = ({ goldColor, goldBorder, goldAccent }) => {
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const toast = useToast();
  const { 
    isConnected, 
    user, 
    channels, 
    activeChannel, 
    messages: discordMessages, 
    loading, 
    error,
    connect, 
    changeChannel, 
    sendMessage: sendDiscordMessage 
  } = useDiscord();
  
  const handleSendMessage = () => {
    if (!message.trim()) return;
    
    if (activeTab === 0) {
      // In-app chat message
      toast({
        title: "Message sent",
        description: "Your message has been sent to the community chat.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } else if (activeTab === 1) {
      // Discord message
      if (isConnected && activeChannel) {
        sendDiscordMessage(message);
      } else {
        toast({
          title: "Discord not connected",
          description: "Please connect your Discord account to send messages.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      }
    }
    
    setMessage('');
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };
  
  // Format Discord timestamp
  const formatDiscordTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <Box 
      position="absolute" 
      top="84px" 
      right="0" 
      w="300px" 
      bg="rgba(13, 16, 25, 0.95)" 
      border="1px solid" 
      borderColor="purple.500"
      borderRadius="15px"
      boxShadow="0 0 15px rgba(149, 128, 255, 0.2)"
      overflowY="auto"
      p={4}
      m={4}
      mt={0}
      h="calc(100vh - 92px)"
    >
      <Tabs variant="soft-rounded" colorScheme="purple" size="sm" isFitted onChange={(index) => setActiveTab(index)}>
        <TabList mb={3}>
          <Tab><Icon as={FaComments} mr={2} />In-App</Tab>
          <Tab><Icon as={FaDiscord} mr={2} />Discord</Tab>
        </TabList>
        
        <TabPanels>
          {/* In-App Chat Tab */}
          <TabPanel p={0}>
            <VStack h="full" spacing={3}>
              <HStack w="full" justify="space-between">
                <Text size="md" color={goldColor} fontWeight="bold">Community Chat</Text>
                <Badge colorScheme="green">5 Online</Badge>
              </HStack>
              
              <Box 
                w="full" 
                h="calc(100vh - 250px)" 
                overflowY="auto" 
                bg="rgba(13, 16, 25, 0.7)" 
                borderRadius="md" 
                p={3}
              >
                {[
                  { id: 1, user: "CryptoWhale", text: "Anyone playing the fortune wheel today?", time: "10:15 PM" },
                  { id: 2, user: "PirateKing", text: "Just won 500 JINU from the lootbox!", time: "10:16 PM" },
                  { id: 3, user: "TreasureHunter", text: "The new DAO proposal is live, make sure to vote", time: "10:22 PM" },
                  { id: 4, user: "You", text: "Thanks for the heads up!", time: "10:23 PM" },
                  { id: 5, user: "NFTCollector", text: "Anyone selling Golden Keys?", time: "10:25 PM" },
                  { id: 6, user: "SolanaFan", text: "Check the marketplace, saw some listed earlier", time: "10:26 PM" }
                ].map(msg => (
                  <Box 
                    key={msg.id} 
                    mb={2} 
                    p={2} 
                    borderRadius="md" 
                    bg={msg.user === "You" ? "rgba(255, 215, 0, 0.1)" : "rgba(255, 255, 255, 0.05)"}
                  >
                    <HStack mb={1}>
                      <Text fontWeight="bold" fontSize="sm">{msg.user}</Text>
                      <Text fontSize="xs" color="gray.500">{msg.time}</Text>
                    </HStack>
                    <Text fontSize="sm">{msg.text}</Text>
                  </Box>
                ))}
              </Box>
              
              <InputGroup>
                <Input 
                  placeholder="Type a message..." 
                  borderColor={goldBorder}
                  _focus={{ borderColor: goldColor }}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <Button 
                  ml={2} 
                  colorScheme="yellow" 
                  bg={goldColor}
                  color="black"
                  _hover={{ bg: goldAccent }}
                  onClick={handleSendMessage}
                >
                  Send
                </Button>
              </InputGroup>
              
              <Box w="full">
                <Text mb={2} fontWeight="bold" color={goldColor} fontSize="sm">Active Users</Text>
                <VStack align="stretch" maxH="150px" overflowY="auto">
                  {[
                    { id: 1, name: "CryptoWhale", avatar: "https://bit.ly/ryan-florence", status: "online" },
                    { id: 2, name: "PirateKing", avatar: "https://bit.ly/sage-adebayo", status: "online" },
                    { id: 3, name: "TreasureHunter", avatar: "https://bit.ly/kent-c-dodds", status: "online" },
                    { id: 4, name: "NFTCollector", avatar: "https://bit.ly/prosper-baba", status: "away" },
                    { id: 5, name: "SolanaFan", avatar: "https://bit.ly/code-beast", status: "offline" }
                  ].map(user => (
                    <HStack key={user.id} py={1}>
                      <Box position="relative">
                        <Avatar size="xs" name={user.name} src={user.avatar} />
                        <Box 
                          position="absolute" 
                          bottom="0" 
                          right="0" 
                          w="8px" 
                          h="8px" 
                          borderRadius="full" 
                          bg={
                            user.status === "online" ? "green.400" : 
                            user.status === "away" ? "yellow.400" : 
                            "gray.500"
                          }
                          border="1px solid"
                          borderColor="gray.800"
                        />
                      </Box>
                      <Text fontSize="sm">{user.name}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </VStack>
          </TabPanel>
          
          {/* Discord Integration Tab */}
          <TabPanel p={0}>
            <VStack h="full" spacing={3} align="stretch">
              <Flex align="center" justify="space-between">
                <Flex align="center">
                  <Icon as={FaDiscord} color="#7289DA" boxSize={6} mr={2} />
                  <Text fontWeight="bold" color="#7289DA">Discord Integration</Text>
                </Flex>
                <Badge colorScheme="purple">LIVE</Badge>
              </Flex>
              
              {/* Discord Server Preview */}
              <Box 
                borderWidth="1px" 
                borderColor="gray.700" 
                borderRadius="md" 
                overflow="hidden"
              >
                <Box bg="#36393F" p={3}>
                  <Flex align="center" mb={3}>
                    {channels.guildInfo && channels.guildInfo.icon ? (
                      <Image 
                        src={channels.guildInfo.icon} 
                        fallbackSrc="https://via.placeholder.com/40"
                        boxSize="40px"
                        borderRadius="full"
                        mr={2}
                      />
                    ) : (
                      <Box 
                        boxSize="40px"
                        borderRadius="full"
                        bg="purple.500"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mr={2}
                      >
                        <Icon as={FaDiscord} color="white" boxSize={5} />
                      </Box>
                    )}
                    <Text fontWeight="bold" color="white">
                      {channels.guildInfo ? channels.guildInfo.name : 'PalmHeist Discord'}
                    </Text>
                  </Flex>
                  
                  <Divider borderColor="gray.600" mb={3} />
                  
                  {/* Discord Channels */}
                  <VStack align="stretch" spacing={1} maxH="300px" overflowY="auto">
                    {loading ? (
                      <Flex justify="center" py={4}>
                        <Spinner color="purple.500" />
                      </Flex>
                    ) : (
                      <>
                        <Text color="gray.400" fontSize="xs" fontWeight="bold" px={2}>TEXT CHANNELS</Text>
                        
                        {/* Group channels by category */}
                        {channels.all && channels.all.length > 0 ? channels.all.map(channel => (
                          <Flex 
                            key={channel.id} 
                            align="center" 
                            py={1} 
                            px={2} 
                            borderRadius="md"
                            bg={activeChannel && channel.id === activeChannel.id ? "#42464D" : "transparent"}
                            _hover={{ bg: !(activeChannel && channel.id === activeChannel.id) && "#42464D" }}
                            cursor="pointer"
                            onClick={() => changeChannel(channel)}
                          >
                            <Icon as={FaHashtag} color={activeChannel && channel.id === activeChannel.id ? "white" : "gray.500"} mr={2} fontSize="xs" />
                            <Text 
                              fontSize="sm" 
                              fontWeight={activeChannel && channel.id === activeChannel.id ? "bold" : "normal"}
                              color={activeChannel && channel.id === activeChannel.id ? "white" : "gray.500"}
                            >
                              {channel.name}
                            </Text>
                          </Flex>
                        )) : (
                          <Text color="gray.500" fontSize="xs" px={2}>No text channels found</Text>
                        )}
                        
                        <Text color="gray.400" fontSize="xs" fontWeight="bold" px={2} mt={2}>VOICE CHANNELS</Text>
                        
                        {channels.voice && channels.voice.length > 0 ? channels.voice.map(channel => (
                          <Flex 
                            key={channel.id} 
                            align="center" 
                            py={1} 
                            px={2} 
                            borderRadius="md"
                            _hover={{ bg: "#42464D" }}
                            cursor="pointer"
                          >
                            <Icon as={FaVolumeUp} color="gray.500" mr={2} fontSize="xs" />
                            <Text fontSize="sm" color="gray.500">{channel.name}</Text>
                            {/* We would need to fetch voice states to show users count */}
                          </Flex>
                        )) : (
                          <Text color="gray.500" fontSize="xs" px={2}>No voice channels found</Text>
                        )}
                      </>
                    )}
                  </VStack>
                </Box>
              </Box>
              
              {/* Discord Messages */}
              {isConnected && activeChannel ? (
                <Box 
                  borderWidth="1px" 
                  borderColor="gray.700" 
                  borderRadius="md" 
                  bg="#36393F" 
                  p={2}
                  h="250px"
                  overflowY="auto"
                >
                  {loading ? (
                    <Flex justify="center" align="center" h="full">
                      <Spinner color="purple.500" />
                    </Flex>
                  ) : discordMessages.length > 0 ? (
                    <VStack spacing={2} align="stretch">
                      {discordMessages.map(msg => (
                        <Box key={msg.id} p={2} _hover={{ bg: "#32353B" }}>
                          <Flex>
                            <Avatar 
                              size="xs" 
                              name={msg.author.username} 
                              src={msg.author.avatar ? `https://cdn.discordapp.com/avatars/${msg.author.id}/${msg.author.avatar}.png` : undefined} 
                              mr={2} 
                            />
                            <Box>
                              <Flex align="center">
                                <Text fontWeight="bold" fontSize="xs" color={msg.author.bot ? "#7289DA" : "white"}>
                                  {msg.author.username}
                                </Text>
                                <Text fontSize="2xs" color="gray.500" ml={2}>
                                  {formatDiscordTime(msg.timestamp)}
                                </Text>
                              </Flex>
                              <Text fontSize="xs" color="gray.300">{msg.content}</Text>
                            </Box>
                          </Flex>
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <Flex justify="center" align="center" h="full">
                      <Text color="gray.500" fontSize="sm">No messages in this channel</Text>
                    </Flex>
                  )}
                </Box>
              ) : null}
              
              {/* Debug Info - Hidden in production */}
              {process.env.NODE_ENV === 'development' && (
                <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={2} fontSize="xs" color="gray.500">
                  <Text>Connected: {isConnected ? 'Yes' : 'No'}</Text>
                  <Text>Active Channel: {activeChannel ? activeChannel.name : 'None'}</Text>
                  <Text>Channels Loaded: {channels.all ? channels.all.length : 0}</Text>
                </Box>
              )}
              
              {/* Message Input or Connect Button */}
              {isConnected ? (
                <InputGroup>
                  <Input 
                    placeholder={activeChannel ? `Message #${activeChannel.name}` : "Select a channel..."}
                    borderColor="gray.700"
                    bg="#40444B"
                    color="white"
                    _focus={{ borderColor: "#7289DA" }}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={!activeChannel}
                  />
                  {/* Hidden debug info for development */}
                  {process.env.NODE_ENV === 'development' && (
                    <Box position="absolute" top="-20px" right="0" fontSize="xs" color="red.500">
                      {activeChannel ? `Active: #${activeChannel.name}` : 'No active channel'}
                    </Box>
                  )}
                  <Button 
                    ml={2} 
                    colorScheme="purple" 
                    bg="#7289DA"
                    _hover={{ bg: "#5E77D4" }}
                    onClick={handleSendMessage}
                    disabled={!activeChannel}
                  >
                    Send
                  </Button>
                </InputGroup>
              ) : (
                <VStack spacing={2} mt={2}>
                  <Button 
                    leftIcon={<FaDiscord />} 
                    colorScheme="purple" 
                    bg="#7289DA" 
                    size="sm" 
                    width="full"
                    onClick={connect}
                  >
                    Connect Discord Account
                  </Button>
                  
                  <Link 
                    href="https://discord.gg/8GnWVtKth6" 
                    isExternal 
                    color="purple.300" 
                    fontSize="sm" 
                    display="flex" 
                    alignItems="center"
                    justifyContent="center"
                  >
                    Join Our Discord Server <Icon as={FaExternalLinkAlt} ml={1} boxSize={3} />
                  </Link>
                </VStack>
              )}
              
              {/* Discord Controls */}
              {isConnected && user && (
                <Flex justify="space-between" mt="auto" bg="#36393F" p={2} borderRadius="md">
                  <Flex align="center">
                    <Avatar 
                      size="xs" 
                      name={user.username} 
                      src={user.avatar ? `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png` : undefined} 
                    />
                    <Text ml={2} fontSize="xs" color="white">{user.username}</Text>
                  </Flex>
                  <HStack spacing={2}>
                    <Icon as={FaMicrophone} color="gray.400" cursor="pointer" _hover={{ color: "white" }} />
                    <Icon as={FaHeadphones} color="gray.400" cursor="pointer" _hover={{ color: "white" }} />
                    <Icon as={FaCog} color="gray.400" cursor="pointer" _hover={{ color: "white" }} />
                  </HStack>
                </Flex>
              )}
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default CommunityChatSidebar;
